import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, styled, SvgIcon, SvgIconProps, Typography } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { type Editor } from '@tiptap/react';
import React, { useCallback, useMemo } from 'react';
import { EColors, getColor } from 'theme';
import { getToolbarButtons, useLinkHandler, useToolbarState } from './utils';

export const Container = styled(Box)(({ theme: { spacing } }) => ({
    minHeight: '30px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${getColor(EColors.bizlyOSBorder)}`,
    boxSizing: 'border-box',
    paddingBottom: spacing(1.25),
    marginBottom: spacing(1.25),
    gap: spacing(1),
}));

export const Divider = styled(Box)({
    height: '20px',
    width: '1px',
    backgroundColor: getColor(EColors.bizlyOSBorder),
});

const AiContainer = styled(Box)(({ theme: { spacing } }) => ({
    display: 'flex',
    gap: spacing(0.5),
    alignItems: 'center',
    backgroundColor: getColor(EColors.inactiveAction, 0.5),
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'not-allowed',
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme: { spacing, shape } }) => ({
    gap: spacing(0.6),
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        height: '20px',
        width: '20px',
        border: 0,
        borderRadius: shape.borderRadius,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0,
        },
    },
}));

const StyledSvgIcon = styled(SvgIcon)<SvgIconProps>({
    fontSize: 16,
    '&.MuiSvgIcon-root': { fontSize: '16px' },
});

const InsertLabel = styled(Typography)({
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: getColor(EColors.pureBlack, 0.5),
    whiteSpace: 'nowrap',
});

const InsertButton = styled('button')(({ theme: { spacing } }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: spacing(0.5, 1.25),
    borderRadius: '999px',
    border: `1px solid ${getColor(EColors.bizlyOSBorder)}`,
    '&:hover': {
        backgroundColor: getColor(EColors.pureBlack, 0.04),
    },
}));

export type ToolbarInsertOption = {
    label: string;
    /** Literal text inserted at the cursor, e.g. a merge-field token like "{first_name}". */
    value: string;
};

type ToolbarProps = {
    editor: Editor | null;
    isAiWrite?: boolean;
    /** Extra buttons that insert literal text at the cursor, shown after a divider (BIZ-2161's
     *  merge-field chips are the first use of this). */
    insertOptions?: ToolbarInsertOption[];
};

function Toolbar({ editor, isAiWrite = false, insertOptions }: ToolbarProps) {
    const { formats, setFormats } = useToolbarState(editor);
    const setLink = useLinkHandler(editor);

    const toolbarButtons = useMemo(() => getToolbarButtons(setLink), [setLink]);

    const handleFormatChange = useCallback(
        (_: React.MouseEvent<HTMLElement>, newFormats: string[]) => {
            setFormats(newFormats);
        },
        [setFormats]
    );

    if (!editor) {
        return null;
    }

    const handleInsert = (value: string) => {
        editor.chain().focus().insertContent(value).run();
    };

    return (
        <Container>
            <StyledToggleButtonGroup
                size="small"
                value={formats}
                onChange={handleFormatChange}
                aria-label="text formatting"
            >
                {toolbarButtons.map(button => (
                    <ToggleButton key={button.id} value={button.id} onClick={() => button.command(editor)}>
                        <StyledSvgIcon component={button.icon} />
                    </ToggleButton>
                ))}
            </StyledToggleButtonGroup>

            {!!insertOptions?.length && (
                <>
                    <Divider />
                    <InsertLabel>Insert:</InsertLabel>
                    {insertOptions.map(option => (
                        <InsertButton key={option.value} type="button" onClick={() => handleInsert(option.value)}>
                            {option.label}
                        </InsertButton>
                    ))}
                </>
            )}

            {isAiWrite && (
                <>
                    <Divider />
                    <AiContainer>
                        <StyledSvgIcon component={AutoAwesomeIcon} color="disabled" />
                        <Typography variant="body2" fontWeight="500" color={getColor(EColors.pureBlack, 0.5)}>
                            Write with AI
                        </Typography>
                    </AiContainer>
                </>
            )}
        </Container>
    );
}

export default Toolbar;
